import { useEffect, useRef, useState } from 'react'

import { AnySchema, ValidationError } from 'yup'
import { ZodError, ZodSchema } from 'zod'

import { UseWebSocketSubscribeParams } from './types'
import { useWebSocketSubscribe } from './useWebSocketSubscribe'
import { getParsedData } from './utils'

interface Params<TResult> extends UseWebSocketSubscribeParams {
  schema: AnySchema<unknown, any, TResult> | ZodSchema<TResult>
  defaultValue?: TResult
}

export function useWebSocketSubscribeWithValidation<TResult>({
  channel,
  schema,
  defaultValue,
  multi,
}: Params<TResult>) {
  const { value, ...rest } = useWebSocketSubscribe({ channel, multi })
  const initialDefaultValues = useRef(defaultValue)
  const [data, setData] = useState<TResult | undefined>(initialDefaultValues.current)
  const [error, setError] = useState<ZodError | ValidationError | null>(null)

  useEffect(() => {
    if (value !== undefined) {
      try {
        const parsedValues = getParsedData(value, schema)

        setData(parsedValues)
        setError(null)
      } catch (validationError: unknown) {
        setData(initialDefaultValues.current)
        setError(validationError as ZodError | ValidationError)
      }
    }
  }, [value, schema])

  return { ...rest, data, error }
}
